<script lang="ts" module>
	export interface HeroButtonProps {
		label: string;
		href?: string;
		arrow?: boolean;
		variant?: ButtonProps['variant'];
	}
</script>

<script lang="ts">
	import classNames from 'classnames';
	import { getContext, type Component } from 'svelte';
	import { twMerge } from 'tailwind-merge';
	import { ChevronRightIcon } from '../../assets/icons/ChevronRightIcon';
	import { getImageSrcset } from '../../utils/getImageSrcset/getImageSrcset';
	import type { ButtonProps } from '../Button';
	import { LinkButton } from '../LinkButton';
	import { Markdown, type MarkdownSource } from '../Markdown';
	import { Txt, type TxtVariant } from '../Txt';
	import { HERO_CONTAINER_PREVIEW_CONTEXT } from './constants/contexts';

	// A <source> pointing here stops the image hidden at this breakpoint from downloading.
	const BLANK_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

	const MOBILE_IMAGE_WIDTHS = [640, 828, 1080, 1440, 2048];
	const DESKTOP_IMAGE_WIDTHS = [1080, 1440, 2048];
	// Only offered to screens that need them (large or high-density), so phones still get a small image.
	const HIGH_RESOLUTION_IMAGE_WIDTHS = [2880, 3840];
	const IMAGE_SIZES = '100vw';

	// The media query, the screen classes and the container classes all switch at the same width
	// (md = 48rem = @md 768px, xl = 80rem = @xl 1280px in heni-websites-config).
	const IMAGE_BREAKPOINTS = {
		md: {
			media: '(min-width: 48rem)',
			mobileImage: 'md:hidden',
			desktopImage: 'md:block',
			previewMobileImage: '@md:hidden',
			previewDesktopImage: '@md:block'
		},
		xl: {
			media: '(min-width: 80rem)',
			mobileImage: 'xl:hidden',
			desktopImage: 'xl:block',
			previewMobileImage: '@xl:hidden',
			previewDesktopImage: '@xl:block'
		}
	};

	let {
		src,
		layerOpacity = undefined,
		mobileSrc = undefined,
		title,
		titleVariant = undefined,
		subtitle = undefined,
		description = undefined,
		description2 = undefined,
		PlatformLogo = undefined,
		align = 'left',
		small = false,
		buttonProps = undefined,
		secondaryButtonProps = undefined,
		dataCy,
		alt,
		classes = {},
		textShadow,
		onclick,
		overline = '',
		isLive = false,
		desktopImageStyle = undefined,
		mobileImageStyle = undefined,
		imageBreakpoint = 'md',
		priority = true,
		highResolution = false,
		...rest
	}: Props = $props();

	interface Props {
		onclick?: (e?: Event) => void;
		class?: string;
		src: string;
		textShadow?: boolean;
		layerOpacity?: number | null | undefined;
		mobileSrc?: string | undefined | null;
		title: string;
		titleVariant?: TxtVariant | undefined;
		subtitle?: string | null | undefined;
		description?: string | null | undefined;
		description2?: MarkdownSource | null | undefined;
		PlatformLogo?: Component | undefined;
		align?: 'center' | 'left';
		small?: boolean | undefined;
		buttonProps?: HeroButtonProps | undefined;
		secondaryButtonProps?: HeroButtonProps | undefined;
		dataCy: string;
		alt: string | null | undefined;
		overline?: string | null | undefined;
		classes?: {
			overline?: string;
			button?: string;
			secondaryButton?: string;
			buttonsContainer?: string;
			title?: string;
			subtitle?: string;
			description?: string;
			mobileImage?: string;
			image?: string;
			container?: string;
		};
		isLive?: boolean;
		desktopImageStyle?: string;
		mobileImageStyle?: string;
		/** Width from which the desktop image replaces the mobile one. */
		imageBreakpoint?: keyof typeof IMAGE_BREAKPOINTS;
		/** Preload the image at high priority; turn off for a hero below the fold. */
		priority?: boolean;
		/** Also offer widths above 2048px, for heroes whose image is not to be compressed. */
		highResolution?: boolean;
	}

	const containerPreview = getContext<boolean | undefined>(HERO_CONTAINER_PREVIEW_CONTEXT) ?? false;

	let breakpoint = $derived(IMAGE_BREAKPOINTS[imageBreakpoint]);
	let mobileImageSrc = $derived(mobileSrc || src);
	let extraImageWidths = $derived(highResolution ? HIGH_RESOLUTION_IMAGE_WIDTHS : []);
	let mobileSrcset = $derived(
		getImageSrcset(mobileImageSrc, [...MOBILE_IMAGE_WIDTHS, ...extraImageWidths])
	);
	let desktopSrcset = $derived(getImageSrcset(src, [...DESKTOP_IMAGE_WIDTHS, ...extraImageWidths]));
	let imageLoadingProps = $derived(
		priority
			? { fetchpriority: 'high' as const, loading: 'eager' as const }
			: { loading: 'lazy' as const }
	);

	let firstButtonVariant = $derived(
		buttonProps?.variant
			? buttonProps?.variant
			: align === 'left' && secondaryButtonProps
				? 'secondary'
				: 'ghost'
	);

	let dataCyPrefix = $derived(`${dataCy}-hero`);
</script>

<svelte:head>
	{#if priority && !containerPreview}
		{#if mobileImageSrc}
			<link
				rel="preload"
				as="image"
				href={mobileImageSrc}
				imagesrcset={mobileSrcset}
				imagesizes={IMAGE_SIZES}
				media={`not all and ${breakpoint.media}`}
				fetchpriority="high"
			/>
		{/if}
		{#if src}
			<link
				rel="preload"
				as="image"
				href={src}
				imagesrcset={desktopSrcset}
				imagesizes={IMAGE_SIZES}
				media={breakpoint.media}
				fetchpriority="high"
			/>
		{/if}
	{/if}
</svelte:head>

<section
	class={twMerge(
		classNames('overflow-hidden relative flex bg-cover @container', {
			'h-[30rem] items-center @lg:h-[33.75rem]': !small,
			'h-[25rem] items-end': small,
			'justify-center': align === 'center'
		}),
		rest.class
	)}
>
	{#if containerPreview}
		{#if mobileImageSrc}
			<img
				class={twMerge(
					'h-full w-full object-cover',
					breakpoint.previewMobileImage,
					classes?.mobileImage
				)}
				{alt}
				src={mobileImageSrc}
				style={mobileImageStyle}
			/>
		{/if}
		{#if src}
			<img
				class={twMerge(
					'hidden h-full w-full object-cover',
					breakpoint.previewDesktopImage,
					classes?.image
				)}
				{alt}
				{src}
				style={desktopImageStyle}
			/>
		{/if}
	{:else}
		<!-- Each image only downloads at its own breakpoint; the other one resolves to BLANK_IMAGE. -->
		{#if mobileImageSrc}
			<picture class="contents">
				<source media={breakpoint.media} srcset={BLANK_IMAGE} />
				<img
					class={twMerge(
						'h-full w-full object-cover',
						breakpoint.mobileImage,
						classes?.mobileImage
					)}
					{alt}
					src={mobileImageSrc}
					srcset={mobileSrcset}
					sizes={IMAGE_SIZES}
					decoding="async"
					{...imageLoadingProps}
					style={mobileImageStyle}
				/>
			</picture>
		{/if}
		{#if src}
			<picture class="contents">
				<source media={breakpoint.media} srcset={desktopSrcset ?? src} sizes={IMAGE_SIZES} />
				<img
					class={twMerge(
						'hidden h-full w-full object-cover',
						breakpoint.desktopImage,
						classes?.image
					)}
					{alt}
					src={BLANK_IMAGE}
					decoding="async"
					{...imageLoadingProps}
					style={desktopImageStyle}
				/>
			</picture>
		{/if}
	{/if}

	<div
		class="absolute h-full w-full"
		style={`background-color: rgb(0 0 0 / ${layerOpacity ?? 0.4})`}
	></div>

	<div
		class={twMerge(
			classNames('absolute mx-auto flex w-full max-w-screen-xl flex-col px-4 sm:px-6', {
				'items-center': align === 'center',
				'left-[50%] translate-x-[-50%]': align === 'left'
			}),
			classes?.container
		)}
	>
		{#if PlatformLogo}
			<PlatformLogo class="mb-3 h-[0.625rem]" color="gray-0" data-cy={`${dataCyPrefix}-logo`} />
		{/if}

		{#if overline}
			<Txt
				variant="label2"
				class={twMerge('mb-2 text-white', classes.overline)}
				dataCy={`${dataCyPrefix}-overline`}
				style={textShadow
					? 'text-shadow: rgba(0, 0, 0, 0.1) 0px 1px 2px, rgba(0, 0, 0, 0.1) 0px 1px 1px, rgba(0, 0, 0, 0.1) 0px 1px 8px'
					: ''}
			>
				{overline}
			</Txt>
		{/if}

		<Txt
			variant={titleVariant || 'h1'}
			dataCy={`${dataCyPrefix}-title`}
			class={twMerge(
				classNames('text-gray-0 font-[500] tracking-normal', {
					'pb-[3rem] lg:pb-[4.5rem]': small,
					'mb-2': !!subtitle || (!subtitle && description),
					'mb-7': !subtitle && !description && !!buttonProps,
					'text-center': align === 'center',
					'max-w-[24ch]': align === 'left'
				}),
				classes?.title
			)}
			style={textShadow
				? 'text-shadow: rgba(0, 0, 0, 0.1) 0px 1px 2px, rgba(0, 0, 0, 0.1) 0px 1px 1px, rgba(0, 0, 0, 0.1) 0px 1px 8px'
				: ''}
		>
			{title}
		</Txt>
		{#if subtitle}
			<Txt
				component="h2"
				dataCy={`${dataCyPrefix}-subtitle`}
				variant="h3"
				class={twMerge(
					classNames('text-gray-0 font-[400]', {
						'mb-3': description,
						'mb-7': !description && !!buttonProps,
						'text-center': align === 'center'
					}),
					classes?.subtitle
				)}
				style={textShadow
					? 'text-shadow: rgba(0, 0, 0, 0.1) 0px 1px 2px, rgba(0, 0, 0, 0.1) 0px 1px 1px, rgba(0, 0, 0, 0.1) 0px 1px 8px'
					: ''}
			>
				{subtitle}
			</Txt>
		{/if}
		{#if description}
			<Txt
				variant="body1"
				dataCy={`${dataCyPrefix}-description`}
				class={twMerge(
					classNames('text-gray-0 max-w-[60ch]', {
						'mb-7': !!buttonProps,
						'text-center': align === 'center'
					}),
					classes?.description
				)}
			>
				{description}
			</Txt>
		{/if}
		{#if description2}
			<Markdown
				size="large"
				source={description2}
				dataCy={`${dataCyPrefix}-description`}
				class={twMerge(
					classNames('[&_p]:mb-0 [&_p]:text-gray-0 [&_a]:text-gray-0 max-w-[60ch]', {
						'mb-7': !!buttonProps,
						'text-center': align === 'center'
					}),
					classes?.description
				)}
			/>
		{/if}
		{#if isLive}
			<Txt
				variant="body1"
				class="mb-7 border border-gray-200 px-2.5 py-1 rounded-full bg-white shadow"
				dataCy={`${dataCyPrefix}-live-now`}
			>
				<span class="h-[10px] w-[10px] inline-block bg-green-500 rounded-full mr-2"></span> Live Now
			</Txt>
		{/if}
		<!-- TODO: find a solution to avoid the repetition, the ChevronRightIcon ends up in the default slot when using #if to conditionally show it -->
		<div class={twMerge('xs:flex-row flex flex-col', classes.buttonsContainer)}>
			{#if buttonProps}
				{#if !buttonProps.arrow}
					<LinkButton
						dataCy={`${dataCyPrefix}-first`}
						href={buttonProps.href}
						variant={firstButtonVariant}
						size="lg"
						class={twMerge(
							classNames({
								'xs:mb-0 mb-2': !!secondaryButtonProps
							}),
							classes?.button
						)}
						newTab={buttonProps.href?.startsWith?.('http')}
						{onclick}
					>
						{buttonProps.label}
					</LinkButton>
				{:else}
					<LinkButton
						href={buttonProps.href}
						dataCy={`${dataCyPrefix}-first`}
						variant={firstButtonVariant}
						size="lg"
						class={twMerge(
							classNames({
								'[&>button>span]:font-[400]': !secondaryButtonProps,
								'xs:mb-0 mb-2': !!secondaryButtonProps
							}),
							classes?.button
						)}
						newTab={buttonProps?.href?.startsWith?.('http')}
						{onclick}
					>
						{buttonProps.label}
						{#snippet trailing()}
							<ChevronRightIcon />
						{/snippet}
					</LinkButton>
				{/if}
			{/if}
			{#if secondaryButtonProps}
				{#if !secondaryButtonProps.arrow}
					<LinkButton
						href={secondaryButtonProps.href}
						dataCy={`${dataCyPrefix}-secondary`}
						newTab={secondaryButtonProps?.href?.startsWith?.('http')}
						variant={secondaryButtonProps?.variant || 'ghost'}
						size="lg"
						class={twMerge('xs:ml-4', classes?.secondaryButton)}
						{onclick}
					>
						{secondaryButtonProps.label}
					</LinkButton>
				{:else}
					<LinkButton
						href={secondaryButtonProps.href}
						dataCy={`${dataCyPrefix}-secondary`}
						newTab={secondaryButtonProps?.href?.startsWith?.('http')}
						variant={secondaryButtonProps?.variant || 'ghost'}
						size="lg"
						class={twMerge('xs:ml-4', classes?.secondaryButton)}
						{onclick}
					>
						{secondaryButtonProps.label}
						{#snippet trailing()}
							<ChevronRightIcon />
						{/snippet}
					</LinkButton>
				{/if}
			{/if}
		</div>
	</div>
</section>
